import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Mail,
  Plus,
  X,
  ChevronRight,
  ChevronLeft,
  Check,
  AlertCircle
} from 'lucide-react';
import { useAlertEmails } from '../../hooks/useAlertEmails';

interface AlertEmailSelectorProps {
  selectedEmails: string[];
  onEmailsChange: (emails: string[]) => void;
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
}

export const AlertEmailSelector: React.FC<AlertEmailSelectorProps> = ({
  selectedEmails,
  onEmailsChange,
  enabled,
  onEnabledChange
}) => {
  const { t } = useTranslation();
  const { emails: availableEmails, loading, isValidEmail, getRecentEmails } = useAlertEmails();
  const [newEmail, setNewEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [autoLoadedRecent, setAutoLoadedRecent] = useState(false);

  // Auto-cargar emails recientes cuando se habilita la notificación
  useEffect(() => {
    if (enabled && !autoLoadedRecent && selectedEmails.length === 0) {
      const recentEmails = getRecentEmails(5);
      if (recentEmails.length > 0) {
        onEmailsChange(recentEmails);
        setAutoLoadedRecent(true);
      }
    }
  }, [enabled, autoLoadedRecent, selectedEmails.length, getRecentEmails, onEmailsChange]);

  // Reset auto-load flag when disabled
  useEffect(() => {
    if (!enabled) {
      setAutoLoadedRecent(false);
    }
  }, [enabled]);

  // Emails disponibles (no seleccionados)
  const unselectedEmails = availableEmails.filter(
    email => !selectedEmails.includes(email)
  );

  const handleAddEmail = () => {
    const trimmedEmail = newEmail.trim().toLowerCase();

    if (!trimmedEmail) {
      setEmailError(t('alerts.email_empty'));
      return;
    }

    if (!isValidEmail(trimmedEmail)) {
      setEmailError(t('alerts.email_invalid'));
      return;
    }

    if (selectedEmails.includes(trimmedEmail)) {
      setEmailError(t('alerts.email_duplicate'));
      return;
    }

    onEmailsChange([...selectedEmails, trimmedEmail]);
    setNewEmail('');
    setEmailError(null);
  };

  const handleRemoveEmail = (email: string) => {
    onEmailsChange(selectedEmails.filter(e => e !== email));
  };

  const handleSelectAvailable = (email: string) => {
    if (!selectedEmails.includes(email)) {
      onEmailsChange([...selectedEmails, email]);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddEmail();
    }
  };

  return (
    <div className="space-y-4">
      {/* Toggle de habilitación */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Mail className="h-5 w-5 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">
            {t('alerts.email_notification')}
          </span>
        </div>
        <button
          onClick={() => onEnabledChange(!enabled)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            enabled ? 'bg-blue-600' : 'bg-gray-300'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              enabled ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      {enabled && (
        <>
          {/* Input para nuevo email */}
          <div className="space-y-2">
            <label className="block text-sm text-gray-600">
              {t('alerts.add_email')}
            </label>
            <div className="flex space-x-2">
              <div className="flex-1 relative">
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => {
                    setNewEmail(e.target.value);
                    setEmailError(null);
                  }}
                  onKeyPress={handleKeyPress}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    emailError ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="email@ejemplo.com"
                />
                {emailError && (
                  <div className="absolute -bottom-5 left-0 flex items-center text-xs text-red-500">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    {emailError}
                  </div>
                )}
              </div>
              <button
                onClick={handleAddEmail}
                className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Panel de dos columnas */}
          <div className="grid grid-cols-2 gap-4 mt-6">
            {/* Columna izquierda: Emails seleccionados */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700 flex items-center">
                <Check className="h-4 w-4 mr-1 text-green-500" />
                {t('alerts.selected_emails')} ({selectedEmails.length})
              </h4>
              <div className="border border-gray-200 rounded-lg h-48 overflow-y-auto bg-green-50">
                {selectedEmails.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-sm text-gray-400">
                    {t('alerts.no_emails_selected')}
                  </div>
                ) : (
                  <div className="p-2 space-y-1">
                    {selectedEmails.map((email) => (
                      <div
                        key={email}
                        className="flex items-center justify-between px-2 py-1.5 bg-white rounded border border-green-200"
                      >
                        <span className="text-sm text-gray-700 truncate flex-1">
                          {email}
                        </span>
                        <button
                          onClick={() => handleRemoveEmail(email)}
                          className="ml-2 p-1 text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Columna derecha: Emails disponibles */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700 flex items-center">
                <Mail className="h-4 w-4 mr-1 text-gray-400" />
                {t('alerts.available_emails')} ({unselectedEmails.length})
              </h4>
              <div className="border border-gray-200 rounded-lg h-48 overflow-y-auto bg-gray-50">
                {loading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
                  </div>
                ) : unselectedEmails.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-sm text-gray-400">
                    {t('alerts.no_emails_available')}
                  </div>
                ) : (
                  <div className="p-2 space-y-1">
                    {unselectedEmails.map((email) => (
                      <button
                        key={email}
                        onClick={() => handleSelectAvailable(email)}
                        className="w-full flex items-center justify-between px-2 py-1.5 bg-white rounded border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors text-left"
                      >
                        <span className="text-sm text-gray-700 truncate flex-1">
                          {email}
                        </span>
                        <ChevronLeft className="h-4 w-4 text-gray-400" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Botón para cargar emails recientes */}
          {!autoLoadedRecent && availableEmails.length > 0 && selectedEmails.length === 0 && (
            <button
              onClick={() => {
                const recent = getRecentEmails(5);
                onEmailsChange(recent);
                setAutoLoadedRecent(true);
              }}
              className="w-full mt-2 px-3 py-2 text-sm text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors flex items-center justify-center"
            >
              <ChevronRight className="h-4 w-4 mr-1" />
              {t('alerts.load_recent_emails')}
            </button>
          )}
        </>
      )}
    </div>
  );
};
